import {
  useState,
  useEffect,
  useRef,
  useCallback,
  createContext,
  useContext,
} from "react";
import {
  ThumbsUp,
  ThumbsDown,
  Bookmark,
  ImageIcon,
  Copy,
  Check,
  FileText,
  Gavel,
  Users,
  Megaphone,
  Award,
  DollarSign,
  GraduationCap,
  FileSearch,
  Building2,
  Globe2,
  Crosshair,
  Newspaper,
  Shield,
  Radar,
  X,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Search as SearchIcon,
} from "lucide-react";
import LoadingSpinner from "./LoadingSpinner";
import { toggleThumb } from "../../api/feed";
import { useRightSidebar } from "../contexts/RightSidebarContext";
import { useDark, getTheme } from "./useNavTheme";
import ShareArticleButton from "./ShareArticleButton";
import { motion } from "framer-motion";

const toBool = (v) => (Array.isArray(v) ? v.length > 0 : !!v);

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const TYPE_BADGES = {
  court_cases: { icon: Gavel, color: "#f59e0b", label: "Court Case" },
  neighbors: { icon: Users, color: "#8b5cf6", label: "Neighbors" },
  press_release: { icon: Megaphone, color: "#10b981", label: "Press Release" },
  gov_award: { icon: Award, color: "#f97316", label: "Gov Award" },
  financial_crawl: { icon: DollarSign, color: "#06b6d4", label: "Financial" },
  edu_crawl: { icon: GraduationCap, color: "#ec4899", label: "Education" },
  investigative_doc: {
    icon: FileSearch,
    color: "#ef4444",
    label: "Investigative",
  },
  company_subsidiary: {
    icon: Building2,
    color: "#6366f1",
    label: "Subsidiary",
  },
  domain_crawl: { icon: Globe2, color: "#14b8a6", label: "Domain Crawl" },
  hunt: { icon: Radar, color: "#0ea5e9", label: "Hunt" },
  apt_news: { icon: Newspaper, color: "#e11d48", label: "APT News" },
  web_citation: { icon: SearchIcon, color: "#c5548ee3", label: "User Hunted" },
};

const TARGET_COLOR = "#22d3ee";
const APT_COLOR = "#f472b6";

const Badge = ({ icon: Icon, color, label, truncate = false }) => (
  <span
    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.625rem] font-normal whitespace-nowrap bg-innerBg${
      truncate ? " max-w-[160px] overflow-hidden" : ""
    }`}
    style={{ color, border: `1px solid ${color}40` }}
  >
    <Icon size={10} className="shrink-0" />
    {truncate ? (
      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
        {label}
      </span>
    ) : (
      label
    )}
  </span>
);

export const TypeBadge = ({ type }) => {
  const badge = TYPE_BADGES[type];
  if (!badge) return null;
  return <Badge icon={badge.icon} color={badge.color} label={badge.label} />;
};

export const TargetBadge = ({ target }) =>
  target ? (
    <Badge icon={Crosshair} color={TARGET_COLOR} label={target} truncate />
  ) : null;

export const AptBadge = ({ aptName }) =>
  aptName ? (
    <Badge icon={Shield} color={APT_COLOR} label={aptName} truncate />
  ) : null;

const ActionBtn = ({ active, label, onClick, children }) => {
  const isActive = toBool(active);
  return (
    <button
      type="button"
      aria-pressed={isActive}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      className={[
        "inline-flex items-center justify-center rounded-full h-9 w-9 border cursor-pointer",
        "outline-none ring-velBlue/40 focus-visible:ring-2 transition-all duration-150 active:scale-90",
        isActive
          ? "bg-velBlue/10 border-velBlue/50 text-velBlue"
          : "bg-innerBg border-surfaceBorder text-mutedText hover:border-velBlue/30 hover:text-mainText",
      ].join(" ")}
    >
      {children}
    </button>
  );
};

const BrandedVideoPlayer = ({ src, poster, onError }) => {
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const idleTimerRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = isMuted;
  }, [isMuted]);

  useEffect(() => () => clearTimeout(idleTimerRef.current), []);

  const armIdleHide = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    setShowControls(true);
    if (!isPlaying) return;
    idleTimerRef.current = setTimeout(() => setShowControls(false), 2200);
  }, [isPlaying]);

  const togglePlay = (e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    setIsMuted((m) => !m);
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    const bar = progressRef.current;
    if (!v || !bar || !duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    v.currentTime = Math.max(0, Math.min(duration, ratio * duration));
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const showOverlay = !isPlaying;

  const overlayBtnIdle =
    "bg-black/45 border-white/15 text-white/85 hover:bg-black/55 hover:border-velBlue/25 hover:text-white";
  const overlayBtnActive = "bg-velBlue/15 border-velBlue/30 text-velBlue/90";

  return (
    <div
      onMouseMove={armIdleHide}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      className="relative w-full overflow-hidden rounded-xl border border-surfaceBorder bg-black"
      style={{ aspectRatio: "9 / 16" }}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        playsInline
        preload="metadata"
        onPlay={() => {
          setIsPlaying(true);
          armIdleHide();
        }}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onError={onError}
        onClick={togglePlay}
        className="h-full w-full cursor-pointer object-contain"
      />

      {showOverlay && (
        <button
          onClick={togglePlay}
          className="group absolute inset-0 flex items-center justify-center bg-black/30 transition hover:bg-black/40"
          aria-label="Play"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/25 bg-black/55 text-white shadow-login-panel backdrop-blur-md transition group-hover:scale-105 group-hover:bg-black/70 group-hover:border-white/40 group-active:scale-95">
            <Play size={22} fill="currentColor" className="ml-0.5" />
          </span>
        </button>
      )}

      <div
        className={[
          "pointer-events-none absolute inset-x-0 bottom-0 px-2.5 pb-2 pt-7 transition-opacity duration-200",
          showControls ? "opacity-100" : "opacity-0",
        ].join(" ")}
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)",
        }}
      >
        <div
          ref={progressRef}
          onClick={handleSeek}
          className="group/progress pointer-events-auto mb-2 flex h-3 w-full cursor-pointer items-center"
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={duration || 0}
          aria-valuenow={currentTime}
        >
          <div className="relative h-1 w-full overflow-hidden rounded-full bg-white/15 transition-colors group-hover/progress:bg-white/25">
            <div
              className="h-full bg-velBlue/70 transition-[width] duration-150"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="pointer-events-auto flex items-center gap-2">
          <button
            onClick={togglePlay}
            className={[
              "rounded-full border p-1.5 backdrop-blur-md transition active:scale-95",
              overlayBtnIdle,
            ].join(" ")}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause size={12} /> : <Play size={12} />}
          </button>

          <span className="text-[10px] font-medium tabular-nums">
            <span className="text-white/90">{formatTime(currentTime)}</span>
            <span className="text-white/40"> / {formatTime(duration)}</span>
          </span>

          <div className="flex-1" />

          <button
            onClick={toggleMute}
            className={[
              "rounded-full border p-1.5 backdrop-blur-md transition active:scale-95",
              isMuted ? overlayBtnIdle : overlayBtnActive,
            ].join(" ")}
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <VolumeX size={12} /> : <Volume2 size={12} />}
          </button>
        </div>
      </div>
    </div>
  );
};

const SKIP_KEYS = new Set([
  "like",
  "dislike",
  "catalog",
  "target_type",
  "document_id",
  "apt_name",
  "screenshot",
  "screenshot_url",
  "screenshot_full",
  "screenshot_status",
  "screenshot_pending",
  "screenshot_attempts",
  "screenshot_updated_at",
  "screenshot_next_retry_at",
  "screenshot_error",
  "target",
  "uuid",
  "indexed_on",
]);
const DEFAULT_OPEN_DEPTH = 1;

const FOCUS_RING = "bg-velBlue/15 ring-1 ring-velBlue/30";
const focusLeaf = (focused) =>
  focused ? `${FOCUS_RING} rounded-md px-0.5` : "";

function getParentPath(path) {
  const i = Math.max(path.lastIndexOf("."), path.lastIndexOf("["));
  return i > 0 ? path.slice(0, i) : null;
}

function getKeyFromPath(path) {
  const dot = path.lastIndexOf(".");
  const bracket = path.lastIndexOf("[");
  if (dot === -1 && bracket === -1) return null;
  if (bracket > dot) return null;
  return path.slice(dot + 1);
}

const TIMESTAMP_KEY_RE =
  /^(timestamp|date|created|updated|published|.*_at|.*_time|.*_timestamp)$/i;

const isTimestampKey = (key) => !!key && TIMESTAMP_KEY_RE.test(key);

function isPlausibleUnixTimestamp(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return false;
  return (n >= 1e9 && n < 1e10) || (n >= 1e12 && n < 1e13);
}

const toMillis = (n) => (n < 1e11 ? n * 1000 : n);

const TIME_UNITS = [
  { threshold: 60, divisor: 1, unit: "second" },
  { threshold: 3600, divisor: 60, unit: "minute" },
  { threshold: 86400, divisor: 3600, unit: "hour" },
  { threshold: 86400 * 7, divisor: 86400, unit: "day" },
  { threshold: 86400 * 30, divisor: 86400 * 7, unit: "week" },
  { threshold: 86400 * 365, divisor: 86400 * 30, unit: "month" },
  { threshold: Infinity, divisor: 86400 * 365, unit: "year" },
];

function formatRelativeTime(ms) {
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const diffSec = (ms - Date.now()) / 1000;
  const absSec = Math.abs(diffSec);
  const { divisor, unit } = TIME_UNITS.find((u) => absSec < u.threshold);
  return rtf.format(Math.round(diffSec / divisor), unit);
}

const formatAbsoluteTime = (ms) =>
  new Date(ms).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

function scrollToPath(path) {
  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-json-path="${CSS.escape(path)}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  });
}

const JsonNavCtx = createContext(null);

const LeafSpan = ({ path, focused, className = "", children }) => (
  <span
    data-json-path={path}
    className={`text-xs ${focusLeaf(focused)} ${className}`}
  >
    {children}
  </span>
);

const ExpandableString = ({
  value,
  previewLength = 100,
  className = "text-xs text-mutedText break-all",
  buttonClassName = "ml-1.5 text-xs text-velBlue hover:underline shrink-0",
}) => {
  const [expanded, setExpanded] = useState(false);
  const shouldCollapse = value.length > previewLength;
  const displayValue =
    expanded || !shouldCollapse ? value : `${value.slice(0, previewLength)}…`;

  return (
    <span className={className}>
      {displayValue}
      {shouldCollapse && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((p) => !p);
          }}
          className={buttonClassName}
        >
          {expanded ? "less" : "more"}
        </button>
      )}
    </span>
  );
};

const ArticleContentString = ({ value, path, focused }) => (
  <span
    data-json-path={path}
    className={`rounded-md px-0.5 min-w-0 ${focused ? FOCUS_RING : ""}`}
  >
    <ExpandableString value={value} previewLength={900} />
  </span>
);

const UrlCopyValue = ({ value, focused, path }) => {
  const [copied, setCopied] = useState(false);
  const [overflowPx, setOverflowPx] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const textRef = useRef(null);
  const viewportRef = useRef(null);

  const shouldAnimate = isHovering && overflowPx > 0;

  const handleMouseEnter = () => {
    const t = textRef.current;
    const v = viewportRef.current;
    if (!t || !v) return;
    const overflow = t.scrollWidth - v.clientWidth;
    setOverflowPx(Math.max(0, overflow));
    setIsHovering(true);
  };

  return (
    <span
      data-json-path={path}
      className={`inline-flex items-center gap-1.5 rounded-md px-0.5 min-w-0 max-w-full ${
        focused ? FOCUS_RING : ""
      }`}
    >
      <span
        ref={viewportRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsHovering(false)}
        className="overflow-hidden min-w-0"
        title={value}
      >
        <motion.span
          ref={textRef}
          className="text-xs text-velBlue/80 whitespace-nowrap inline-block"
          animate={{ x: shouldAnimate ? -overflowPx : 0 }}
          transition={
            shouldAnimate
              ? {
                  duration: Math.max(1.5, overflowPx / 50),
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "linear",
                }
              : { duration: 0.3, ease: "easeOut" }
          }
        >
          {value}
        </motion.span>
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        className="shrink-0 text-mutedText hover:text-velBlue transition-colors"
        aria-label="Copy URL"
      >
        {copied ? <Check size={10} /> : <Copy size={10} />}
      </button>
    </span>
  );
};

const TimestampValue = ({ value, path, focused }) => {
  const ms = toMillis(value);
  const relative = formatRelativeTime(ms);
  const absolute = formatAbsoluteTime(ms);
  return (
    <span
      data-json-path={path}
      className={`text-xs break-words rounded-md px-0.5 ${focused ? FOCUS_RING : ""}`}
      title={`${absolute} · raw: ${value}`}
    >
      <span className="text-velBlue">{relative}</span>
      <span className="text-mutedText/70"> · {absolute}</span>
    </span>
  );
};

const JsonValue = ({ value, path, depth }) => {
  const ctx = useContext(JsonNavCtx);

  const open = ctx
    ? ctx.openState.has(path)
      ? ctx.openState.get(path)
      : depth < DEFAULT_OPEN_DEPTH
    : depth < DEFAULT_OPEN_DEPTH;

  const focused = ctx?.focusedPath === path;

  const register = (hasChildren) =>
    ctx?.nodeOrderRef.current.push({ path, depth, hasChildren });

  const toggle = (e) => {
    e?.stopPropagation?.();
    ctx?.togglePath(path, depth);
    ctx?.setFocusedPath(path);
  };

  const collapsibleCls = focused
    ? `${FOCUS_RING} text-mainText rounded-md px-0.5`
    : "rounded-md px-0.5 text-mutedText hover:text-mainText";

  if (value === null || value === undefined || value === "") {
    register(false);
    return (
      <LeafSpan path={path} focused={focused} className="text-mutedText">
        —
      </LeafSpan>
    );
  }

  if (typeof value === "boolean") {
    register(false);
    return (
      <LeafSpan path={path} focused={focused} className="text-velBlue">
        {String(value)}
      </LeafSpan>
    );
  }

  if (typeof value === "number") {
    register(false);
    const key = getKeyFromPath(path);
    if (isTimestampKey(key) && isPlausibleUnixTimestamp(value)) {
      return <TimestampValue value={value} path={path} focused={focused} />;
    }
    return (
      <LeafSpan path={path} focused={focused} className="text-velBlue">
        {value}
      </LeafSpan>
    );
  }

  if (typeof value === "string") {
    register(false);

    const key = getKeyFromPath(path);
    if (isTimestampKey(key) && /^\d+$/.test(value)) {
      const n = Number(value);
      if (isPlausibleUnixTimestamp(n)) {
        return <TimestampValue value={n} path={path} focused={focused} />;
      }
    }

    if (value.startsWith("http://") || value.startsWith("https://")) {
      return <UrlCopyValue value={value} focused={focused} path={path} />;
    }

    const isContentField = key === "content";

    if (isContentField) {
      return (
        <ArticleContentString value={value} path={path} focused={focused} />
      );
    }

    if (value.length > 100) {
      return (
        <span
          data-json-path={path}
          className={`rounded-md px-0.5 min-w-0 ${focused ? FOCUS_RING : ""}`}
        >
          <ExpandableString value={value} />
        </span>
      );
    }

    return (
      <LeafSpan
        path={path}
        focused={focused}
        className="text-mutedText break-all min-w-0"
      >
        {value}
      </LeafSpan>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      register(false);
      return <span className="text-xs text-mutedText">—</span>;
    }
    register(true);
    return (
      <div className="min-w-0">
        <button
          data-json-path={path}
          onClick={toggle}
          className={`text-xs flex items-center gap-1 transition-colors outline-none ${collapsibleCls}`}
        >
          <span>{open ? "▾" : "▸"}</span>
          <span className="text-mutedText/60">{value.length} items</span>
        </button>
        {open && (
          <div className="pl-3 border-l border-surfaceBorder ml-1 mt-1.5 flex flex-col gap-1.5 min-w-0">
            {value.map((v, i) => (
              <div key={i} className="flex gap-2 items-start min-w-0">
                <span className="text-xs text-mutedText/50 shrink-0">{i}</span>
                <div className="min-w-0 flex-1 text-xs leading-4">
                  <JsonValue
                    value={v}
                    path={`${path}[${i}]`}
                    depth={depth + 1}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value).filter(([k]) => !SKIP_KEYS.has(k));
    if (entries.length === 0) {
      register(false);
      return <span className="text-xs text-mutedText">—</span>;
    }
    register(true);
    return (
      <div className="min-w-0">
        <button
          data-json-path={path}
          onClick={toggle}
          className={`text-xs flex items-center gap-1 transition-colors outline-none ${collapsibleCls}`}
        >
          <span>{open ? "▾" : "▸"}</span>
          <span className="text-mutedText/60">{entries.length} fields</span>
        </button>
        {open && (
          <div className="pl-3 border-l border-surfaceBorder ml-1 mt-1.5 flex flex-col gap-1.5 min-w-0">
            {entries.map(([k, v]) => (
              <div key={k} className="flex gap-2 items-start min-w-0">
                <span className="text-xs font-medium text-mainText shrink-0">
                  {k}:
                </span>
                <div className="min-w-0 flex-1 text-xs leading-4">
                  <JsonValue
                    value={v}
                    path={`${path}.${k}`}
                    depth={depth + 1}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  register(false);
  return <span className="text-xs text-mutedText">{String(value)}</span>;
};

const JsonTree = ({ data }) => {
  const ctx = useContext(JsonNavCtx);
  if (ctx) ctx.nodeOrderRef.current = [];

  const entries = Object.entries(data).filter(([k]) => !SKIP_KEYS.has(k));

  return (
    <div className="flex flex-col gap-3 min-w-0">
      {entries.map(([k, v]) => {
        const isComplex =
          v !== null && typeof v === "object"
            ? Array.isArray(v)
              ? v.length > 0
              : Object.keys(v).length > 0
            : false;
        const path = `root.${k}`;
        return isComplex ? (
          <div key={k} className="flex flex-col gap-0.5 min-w-0">
            <span className="text-xs font-medium text-mainText">{k}:</span>
            <div className="pl-2 min-w-0">
              <JsonValue value={v} path={path} depth={1} />
            </div>
          </div>
        ) : (
          <div key={k} className="flex gap-2 items-start min-w-0">
            <span className="text-xs font-medium text-mainText shrink-0">
              {k}:
            </span>
            <div className="min-w-0 flex-1 text-xs leading-4">
              <JsonValue value={v} path={path} depth={1} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const JsonTreeWithNav = ({ data }) => {
  const [openState, setOpenState] = useState(new Map());
  const [focusedPath, setFocusedPath] = useState(null);
  const nodeOrderRef = useRef([]);
  const containerRef = useRef(null);

  const togglePath = useCallback((path, depth) => {
    setOpenState((prev) => {
      const next = new Map(prev);
      const current = prev.has(path)
        ? prev.get(path)
        : depth < DEFAULT_OPEN_DEPTH;
      next.set(path, !current);
      return next;
    });
  }, []);

  const isOpen = useCallback(
    (path, depth) =>
      openState.has(path) ? openState.get(path) : depth < DEFAULT_OPEN_DEPTH,
    [openState],
  );

  useEffect(() => {
    containerRef.current?.focus({ preventScroll: true });
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      const order = nodeOrderRef.current;
      if (order.length === 0) return;

      const navKeys = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "w",
        "a",
        "s",
        "d",
        "W",
        "A",
        "S",
        "D",
      ];
      if (!navKeys.includes(e.key)) return;

      const currentIdx = focusedPath
        ? order.findIndex((n) => n.path === focusedPath)
        : -1;

      if (currentIdx === -1) {
        e.preventDefault();
        setFocusedPath(order[0].path);
        scrollToPath(order[0].path);
        return;
      }

      const current = order[currentIdx];
      const key = e.key.toLowerCase();

      switch (key) {
        case "s":
        case "arrowdown": {
          e.preventDefault();
          const next = order[Math.min(currentIdx + 1, order.length - 1)];
          if (next && next.path !== current.path) {
            setFocusedPath(next.path);
            scrollToPath(next.path);
          }
          break;
        }
        case "w":
        case "arrowup": {
          e.preventDefault();
          const prev = order[Math.max(currentIdx - 1, 0)];
          if (prev && prev.path !== current.path) {
            setFocusedPath(prev.path);
            scrollToPath(prev.path);
          }
          break;
        }
        case "d":
        case "arrowright": {
          e.preventDefault();
          if (!current.hasChildren) break;
          if (!isOpen(current.path, current.depth)) {
            togglePath(current.path, current.depth);
          } else {
            const child = order[currentIdx + 1];
            if (child && child.depth > current.depth) {
              setFocusedPath(child.path);
              scrollToPath(child.path);
            }
          }
          break;
        }
        case "a":
        case "arrowleft": {
          e.preventDefault();
          if (current.hasChildren && isOpen(current.path, current.depth)) {
            togglePath(current.path, current.depth);
          } else {
            const parent = getParentPath(current.path);
            if (parent) {
              setFocusedPath(parent);
              scrollToPath(parent);
            }
          }
          break;
        }
        default:
          break;
      }
    },
    [focusedPath, isOpen, togglePath],
  );

  const ctx = {
    openState,
    togglePath,
    focusedPath,
    setFocusedPath,
    nodeOrderRef,
  };

  return (
    <JsonNavCtx.Provider value={ctx}>
      <div
        ref={containerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className="outline-none min-w-0"
      >
        <JsonTree data={data} />
      </div>
    </JsonNavCtx.Provider>
  );
};

const isABInstance = process.env.REACT_APP_IS_AB === "true";

const getArticleVideoSrc = (article) => article?.video || "";
const getArticleScreenshotSrc = (article) => article?.screenshot_full || "";
const isScreenshotProcessing = (article) => {
  if (!article || getArticleScreenshotSrc(article)) return false;
  const status = article.screenshot_status || "missing";
  return (
    article.screenshot_pending === true ||
    status === "queued" ||
    status === "processing"
  );
};
const getDefaultDetailView = (article, showVideoTab = isABInstance) => {
  if (showVideoTab && getArticleVideoSrc(article)) return "video";
  if (getArticleScreenshotSrc(article) || isScreenshotProcessing(article)) {
    return "screenshot";
  }
  return "info";
};

const ArticleDetailPanel = ({ article, cardId, t, onInteract, onClose }) => {
  const showVideoTab = isABInstance;
  const [viewMode, setViewMode] = useState(() =>
    getDefaultDetailView(article, showVideoTab),
  );
  const [localLike, setLocalLike] = useState(toBool(article?.like));
  const [localDislike, setLocalDislike] = useState(toBool(article?.dislike));
  const [localCatalog, setLocalCatalog] = useState(toBool(article?.catalog));
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const { setIsRightOpen } = useRightSidebar();
  const contentRef = useRef(null);

  const resetMedia = () => {
    setImgLoaded(false);
    setImgError(false);
    setVideoError(false);
  };

  useEffect(() => {
    if (!article) return;
    resetMedia();
    setViewMode(getDefaultDetailView(article, showVideoTab));
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [
    article?.document_id,
    article?.video,
    article?.screenshot_full,
    article?.screenshot_status,
    article?.screenshot_pending,
    showVideoTab,
  ]);

  useEffect(() => {
    setLocalLike(toBool(article?.like));
    setLocalDislike(toBool(article?.dislike));
    setLocalCatalog(toBool(article?.catalog));
  }, [article?.like, article?.dislike, article?.catalog]);

  if (!article) return null;

  const docId = article.document_id;
  const videoSrc = getArticleVideoSrc(article);
  const screenshotSrc = getArticleScreenshotSrc(article);
  const screenshotStatus = screenshotSrc
    ? "ready"
    : article.screenshot_status || "missing";
  const screenshotProcessing =
    !screenshotSrc &&
    (article.screenshot_pending === true ||
      screenshotStatus === "queued" ||
      screenshotStatus === "processing");
  const screenshotFailed = !screenshotSrc && screenshotStatus === "failed";
  const screenshotMessage = screenshotProcessing
    ? "Screenshot is being captured…"
    : screenshotFailed
      ? "Screenshot unavailable"
      : "No screenshot available";
  const canViewVideo = !!videoSrc && !videoError;
  const canViewScreenshot =
    (!!screenshotSrc && !imgError) || screenshotProcessing;

  const applyAction = (action) => {
    const current = {
      like: localLike,
      dislike: localDislike,
      catalog: localCatalog,
    };
    const next = { ...current, [action]: !current[action] };

    toggleThumb({ document_id: docId, action });

    const opposite =
      action === "like" ? "dislike" : action === "dislike" ? "like" : null;
    if (opposite && next[action] && current[opposite]) {
      next[opposite] = false;
      toggleThumb({ document_id: docId, action: opposite });
    }

    setLocalLike(next.like);
    setLocalDislike(next.dislike);
    setLocalCatalog(next.catalog);
    onInteract?.(cardId, docId, next);
  };

  const switchMode = (mode) => {
    if (mode === "video" && (!showVideoTab || !canViewVideo)) return;
    if (mode === "screenshot" && !canViewScreenshot) return;
    setViewMode(mode);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const modeBtn = (mode, Icon, label, disabled = false) => {
    const active = viewMode === mode;
    return (
      <button
        type="button"
        disabled={disabled}
        aria-disabled={disabled}
        onClick={() => switchMode(mode)}
        className={[
          "flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-normal flex-1",
          "transition-all relative z-10 outline-none",
          disabled
            ? "text-mutedText/30 cursor-not-allowed"
            : active
              ? "text-mainText cursor-pointer"
              : "text-mutedText hover:text-mainText cursor-pointer",
        ].join(" ")}
      >
        <Icon size={12} className="shrink-0" />
        <span>{label}</span>
      </button>
    );
  };

  const handleVideoError = () => {
    setVideoError(true);
    setViewMode(screenshotSrc && !imgError ? "screenshot" : "info");
  };

  const handleScreenshotError = () => {
    setImgError(true);
    setViewMode("info");
  };

  const renderDetails = () => (
    <div className="p-4">
      <JsonTreeWithNav data={article} />
    </div>
  );

  const renderScreenshot = () => {
    if (screenshotSrc && !imgError) {
      return (
        <div className="p-4 flex flex-col items-center gap-3">
          {!imgLoaded && (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner />
            </div>
          )}
          <img
            src={screenshotSrc}
            alt="Screenshot"
            className="w-full rounded-xl border border-surfaceBorder"
            style={{ display: imgLoaded ? "block" : "none" }}
            onLoad={() => setImgLoaded(true)}
            onError={handleScreenshotError}
            draggable={false}
          />
        </div>
      );
    }

    if (!screenshotProcessing) return renderDetails();

    return (
      <div className="p-4 flex flex-col items-center gap-3">
        <div className="flex flex-col items-center gap-3 py-10 text-center text-xs text-mutedText">
          <LoadingSpinner />
          <p>{screenshotMessage}</p>
          <p className="max-w-[220px] leading-relaxed">
            You can keep reading the source details while we capture the page.
          </p>
          {article.url && (
            <a
              href={article.url}
              target="_blank"
              rel="noreferrer"
              className="text-velBlue hover:underline"
            >
              Open original source
            </a>
          )}
        </div>
      </div>
    );
  };

  const renderVideo = () => {
    if (!videoSrc || videoError) {
      return canViewScreenshot ? renderScreenshot() : renderDetails();
    }

    return (
      <div className="p-4 flex flex-col items-center gap-3">
        <BrandedVideoPlayer
          key={`${article.document_id}-video`}
          src={videoSrc}
          poster={screenshotSrc || undefined}
          onError={handleVideoError}
        />
      </div>
    );
  };

  const tabCount = showVideoTab ? 3 : 2;
  const activeTabIndex =
    viewMode === "video" && showVideoTab
      ? 2
      : viewMode === "screenshot"
        ? 1
        : 0;
  const tabWidth = `calc(${100 / tabCount}% - 4px)`;
  const tabLeft =
    activeTabIndex === 0
      ? 2
      : `calc(${(100 / tabCount) * activeTabIndex}% + 2px)`;

  return (
    <div className="flex flex-col h-full bg-surface/80 backdrop-blur-md text-mainText font-poppins font-normal">
      <div className="flex items-center gap-2 border-b border-surfaceBorder px-4 pt-8 py-3">
        <div className="relative flex items-center rounded-xl bg-innerBg p-0.5 flex-1">
          <motion.div
            className="absolute rounded-lg bg-surface border border-surfaceBorder"
            style={{ top: 2, bottom: 2, width: tabWidth }}
            animate={{ left: tabLeft }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
          />
          {modeBtn("info", FileText, "Details")}
          {modeBtn("screenshot", ImageIcon, "Screenshot", !canViewScreenshot)}
          {showVideoTab && modeBtn("video", Play, "Video", !canViewVideo)}
        </div>
        <button
          type="button"
          aria-label="Close sidebar"
          onClick={() => (onClose ? onClose() : setIsRightOpen(false))}
          className="shrink-0 flex h-7 w-7 items-center justify-center rounded-lg text-mutedText
                     hover:bg-innerBg hover:text-mainText transition cursor-pointer outline-none
                     ring-velBlue/40 focus-visible:ring-2"
        >
          <X size={13} />
        </button>
      </div>

      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ scrollbarWidth: "thin" }}
      >
        {showVideoTab && viewMode === "video"
          ? renderVideo()
          : viewMode === "screenshot"
            ? renderScreenshot()
            : renderDetails()}
      </div>

      <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-surfaceBorder">
        <ActionBtn
          active={localLike}
          label="Like"
          onClick={() => applyAction("like")}
        >
          <ThumbsUp size={16} strokeWidth={1.5} />
        </ActionBtn>
        <ActionBtn
          active={localDislike}
          label="Dislike"
          onClick={() => applyAction("dislike")}
        >
          <ThumbsDown size={16} strokeWidth={1.5} />
        </ActionBtn>
        <ActionBtn
          active={localCatalog}
          label="Bookmark"
          onClick={() => applyAction("catalog")}
        >
          <Bookmark size={16} strokeWidth={1.5} />
        </ActionBtn>
        <ShareArticleButton
          documentId={docId}
          article={article}
          variant="pill"
          iconSize={16}
        />
      </div>
    </div>
  );
};

export const useArticleSidebar = (articleCards, handleInteract) => {
  const dark = useDark();
  const t = getTheme(dark);
  const {
    isRightOpen,
    setRightComponent,
    setIsRightOpen,
    setRightSidebarMode,
    rightSidebarMode,
  } = useRightSidebar();
  const [activeCardId, setActiveCardId] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);

  const handleCardClick = useCallback(
    (cardId, article) => {
      setActiveCardId(cardId);
      setSelectedCardId(cardId);
      setRightSidebarMode(null);
      setIsRightOpen(true);
      setRightComponent(
        <ArticleDetailPanel
          article={article}
          cardId={cardId}
          t={getTheme(document.documentElement.classList.contains("dark"))}
          onInteract={handleInteract}
        />,
      );
    },
    [setRightComponent, setIsRightOpen, setRightSidebarMode, handleInteract],
  );

  useEffect(() => {
    if (!selectedCardId || !isRightOpen) return;
    if (
      rightSidebarMode === "team-reply" ||
      rightSidebarMode === "code-editor" ||
      rightSidebarMode === "public-citation"
    )
      return;
    const entry = articleCards.find((e) => e.id === selectedCardId);
    if (!entry) return;
    setRightComponent(
      <ArticleDetailPanel
        article={entry.article}
        cardId={selectedCardId}
        t={t}
        onInteract={handleInteract}
      />,
    );
  }, [articleCards, selectedCardId, isRightOpen, t, rightSidebarMode]);

  useEffect(() => {
    if (!isRightOpen) setActiveCardId(null);
  }, [isRightOpen]);

  return {
    activeCardId,
    setActiveCardId,
    selectedCardId,
    setSelectedCardId,
    handleCardClick,
  };
};

export default ArticleDetailPanel;
